import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import usePageMeta from '../hooks/usePageMeta';
import '../styles/static-pages.css';

const policySections = [
  {
    title: '1. Purpose',
    paragraphs: [
      'Risqly operates a cybersecurity and vendor-risk platform that processes company information, user account information, vendor relationship data, policy documents, security scan results, and related operational metadata.',
      'The purpose of this policy is to define how Risqly protects data, manages security risk, governs access, handles incidents, reviews suppliers, and maintains evidence for its own security posture.',
      'This policy supports GDPR-aligned data protection principles including lawfulness, fairness, transparency, purpose limitation, data minimisation, storage limitation, integrity, confidentiality, and accountability.',
    ],
  },
  {
    title: '2. Framework Alignment',
    paragraphs: [
      'Risqly does not claim external certification under ISO 27001, SOC 2, NIST, CIS, or any similar framework unless such certification is separately published by Risqly.',
      'Risqly uses recognised security and privacy frameworks as guidance for internal governance and control design.',
    ],
    bullets: [
      'ISO/IEC 27001-style governance for ownership, access control, supplier management, incident handling, backup, logging, and review.',
      'NIST Cybersecurity Framework-style structure for identifying, protecting, detecting, responding to, and recovering from security risk.',
      'CIS Controls-style technical baseline for access control, secure configuration, vulnerability awareness, logging, account management, and data protection.',
      'GDPR accountability principles for documented processing purposes, processor review, appropriate safeguards, data subject rights, and breach response.',
    ],
  },
  {
    title: '3. Data Covered by This Policy',
    paragraphs: [
      'This policy applies to data processed through Risqly websites, applications, APIs, dashboards, public company profiles, scoring systems, operational tooling, and support workflows.',
    ],
    bullets: [
      'Company profile data such as company name, domain, website, industry, country, description, and verification status.',
      'User account data such as name, email address, authentication identifiers, role, company association, and onboarding state.',
      'Vendor relationship data including declared supplier and customer relationships and dependency metadata.',
      'Security score data including outside-in scan results, policy score data, vendor-network score data, score history, and distribution metrics.',
      'Uploaded policy documents and related metadata.',
      'Operational data including logs, IP addresses, browser and device metadata, request metadata, error logs, audit events, and support communications.',
      'Public website and analytics data where applicable.',
    ],
  },
  {
    title: '4. Security Governance',
    paragraphs: [
      'Risqly assigns security accountability to management. Security responsibilities include maintaining this policy, reviewing material security risks, reviewing production access, reviewing suppliers, coordinating incident response, and maintaining evidence of important security decisions.',
      'Policy exceptions must be approved by management and documented with a reason, owner, expiry date, and remediation plan.',
    ],
  },
  {
    title: '5. Access Control',
    paragraphs: [
      'Risqly follows least-privilege access principles. Access to production systems, databases, storage, authentication systems, and administrative tools is limited to people or service providers who need access for their role.',
    ],
    bullets: [
      'Individual accounts should be used where supported.',
      'Shared accounts should be avoided where practical.',
      'Administrative access should be limited and reviewed periodically.',
      'Access should be removed when no longer needed.',
      'Authentication should use strong credentials and, where available, multi-factor authentication.',
      'Access to uploaded policy documents should be limited to authorised users and operational personnel who need access to provide or maintain the service.',
    ],
  },
  {
    title: '6. Data Protection and Confidentiality',
    paragraphs: [
      'Risqly protects confidential data using technical and organisational measures appropriate to the risk. Risqly does not sell personal data.',
    ],
    bullets: [
      'Encrypted transport for web traffic and API traffic where supported by the relevant system.',
      'Authentication and authorisation controls.',
      'Role-based or need-based administrative access.',
      'Separation between public company profile data and private dashboard or company data.',
      'Secure storage services for uploaded documents.',
      'Logging of relevant operational activity.',
      'Backups or managed recovery mechanisms for critical systems.',
      'Processor and supplier review before using services that process personal or confidential data.',
    ],
  },
  {
    title: '7. Policy Documents and Uploaded Files',
    paragraphs: [
      'Uploaded policy documents may contain confidential company information and personal data. Risqly processes uploaded documents to store them for a customer account, analyse policy maturity, calculate or support policy compliance scoring, and display appropriate score-related results where applicable.',
      'Risqly should not publicly display the contents of uploaded policy documents unless the customer has clearly authorised publication. Public profile pages may display derived policy score details such as framework coverage, control coverage, specificity, governance, execution, and policy penalty.',
    ],
  },
  {
    title: '8. Supplier and Processor Management',
    paragraphs: [
      'Risqly uses third-party providers to operate the platform, including providers for hosting, database services, authentication, storage, email or communications, analytics, and operational services where applicable.',
      'Before using a provider that processes personal or confidential data, Risqly reviews whether the provider is appropriate for the intended processing.',
      'Where personal data is transferred outside the EU/EEA, Risqly uses appropriate transfer safeguards where required, such as Standard Contractual Clauses or another valid transfer mechanism.',
    ],
    bullets: [
      'Security documentation made available by the provider.',
      'Data processing terms or data processing agreement.',
      'Location of processing and transfer mechanism.',
      'Access-control features.',
      'Logging, backup, and availability features.',
      'Whether the provider processes data for its own purposes or only under Risqly instructions.',
    ],
  },
  {
    title: '9. Logging and Monitoring',
    paragraphs: [
      'Risqly maintains operational logs to support security, debugging, abuse prevention, service reliability, and incident investigation. Logs should be limited to what is reasonably necessary and should avoid unnecessary sensitive content where possible.',
    ],
    bullets: [
      'Authentication and session events.',
      'API and backend errors.',
      'Security scan execution metadata.',
      'Administrative actions.',
      'Upload and download events for policy documents.',
      'System health and availability events.',
    ],
  },
  {
    title: '10. Vulnerability and Change Management',
    paragraphs: [
      'Risqly aims to reduce security risk in application code, dependencies, infrastructure, and configuration. Security-relevant changes should be reviewed before deployment where practical.',
    ],
    bullets: [
      'Keeping dependencies reasonably current.',
      'Reviewing code changes before production deployment where practical.',
      'Avoiding hardcoded secrets in source code.',
      'Using environment variables or managed secret storage for sensitive configuration.',
      'Removing unused credentials and rotating credentials when compromise is suspected.',
      'Investigating serious security reports or vulnerabilities affecting Risqly systems or suppliers.',
    ],
  },
  {
    title: '11. Backups and Recovery',
    paragraphs: [
      'Risqly uses managed infrastructure and backup or recovery mechanisms to support recovery from technical failure, accidental deletion, or security incidents.',
    ],
    bullets: [
      'Important production data should have a recovery path.',
      'Backups or managed restore functionality should be reviewed periodically.',
      'Recovery actions should be tested or verified when practical.',
      'Backup access should be restricted.',
      'Backup retention should be proportionate to business, legal, and security needs.',
    ],
  },
  {
    title: '12. Incident Response',
    paragraphs: [
      'Risqly maintains an incident response process for suspected or confirmed security incidents. For personal data breaches, Risqly assesses whether notification to a supervisory authority or affected individuals is required.',
      'Where GDPR applies, Risqly aims to notify the relevant supervisory authority within 72 hours when notification is legally required and feasible.',
    ],
    bullets: [
      'Identify and triage the event.',
      'Assign an owner.',
      'Contain the issue where possible.',
      'Assess affected systems, data, users, and suppliers.',
      'Preserve relevant logs and evidence.',
      'Remediate the root cause where practical.',
      'Communicate with affected customers, users, providers, or authorities where required.',
      'Perform a post-incident review for material incidents.',
    ],
  },
  {
    title: '13. Data Subject Rights',
    paragraphs: [
      'Where Risqly acts as a controller, individuals may have GDPR rights including access, rectification, erasure, restriction, objection, portability, withdrawal of consent where processing is based on consent, and the right to lodge a complaint with a supervisory authority.',
    ],
    bullets: [
      'Verify the requester identity where necessary.',
      'Review whether Risqly is controller or processor for the relevant data.',
      'Respond within applicable legal deadlines.',
      'Record the request and outcome.',
      'Coordinate with customers or processors where necessary.',
    ],
  },
  {
    title: '14. Data Retention',
    paragraphs: [
      'Risqly retains data only as long as reasonably needed for the purpose it was collected, unless a longer period is required for legal, security, accounting, dispute, or operational reasons.',
    ],
    bullets: [
      'Account data is retained while the account or company profile is active, and for a reasonable period after closure where needed.',
      'Uploaded policy documents are retained while the customer account or company workspace requires them, unless deleted earlier.',
      'Security score history may be retained to preserve score trend, auditability, and network-risk context.',
      'Operational logs are retained only as long as needed for security, troubleshooting, and reliability.',
      'Support communications are retained as long as needed to handle the request and protect legitimate interests.',
      'Data may be anonymised or aggregated where Risqly no longer needs identifiable data.',
    ],
  },
  {
    title: '15. Public Scores and Transparency',
    paragraphs: [
      'Risqly publishes or displays security score information to help organisations understand vendor and ecosystem risk. Risqly should distinguish between public company profile information, customer-provided private information, derived score information, and internal evidence or raw operational data.',
      'Risqly should avoid publishing confidential customer documents, private vendor lists, or sensitive operational details unless publication has been authorised or is necessary for the service as clearly described to the customer.',
      'Where scores are derived from automated analysis, Risqly should provide meaningful context about what the score reflects and should avoid presenting the score as a guarantee of security.',
    ],
  },
  {
    title: '16. AI and Automated Analysis',
    paragraphs: [
      'Risqly may use automated analysis to evaluate security signals, uploaded policy documents, public technical posture, vendor-network exposure, and score distributions.',
      'Automated analysis should be used to support risk assessment, not to make unsupported claims about an organisation\'s complete security condition.',
      'Where third-party AI or automation providers are used to process customer data, Risqly should review the provider data protection terms and avoid sending more data than necessary for the function.',
      'Risqly should not state that customer data is excluded from model training unless that is confirmed by the relevant provider terms for the specific service used.',
    ],
  },
  {
    title: '17. Employee and Contractor Expectations',
    paragraphs: [
      'Anyone with access to Risqly systems or confidential data must use access only for authorised business purposes, protect credentials and devices, report suspected incidents promptly, avoid unnecessary copying or sharing of confidential data, follow this policy and related procedures, and return or delete confidential information when access is no longer required.',
    ],
  },
  {
    title: '18. Review and Evidence',
    paragraphs: [
      'Risqly maintains evidence proportionate to its size and risk profile. This policy should be reviewed at least annually and after material changes.',
    ],
    bullets: [
      'Policy review dates.',
      'Supplier review records.',
      'Access review records.',
      'Incident records.',
      'Backup and recovery checks.',
      'Security-relevant change records.',
      'Security scan and score calculation records.',
      'Data subject request records.',
      'Processor and transfer documentation.',
    ],
  },
  {
    title: '19. Contact',
    paragraphs: [
      'Security, privacy, or data protection questions may be submitted through the Risqly Contact page. For GDPR or privacy matters, Risqly should identify whether it acts as controller or processor for the relevant data before responding.',
    ],
  },
];

function SecurityGovernancePolicy() {
  usePageMeta({
    title: 'Information Security and Privacy Governance Policy',
    description: 'How Risqly governs security, privacy, access control, supplier review, incident response, policy documents, and score transparency.',
  });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="static-page-root">
      <Header />
      <main className="static-page-main static-page-main--wide">
        <section className="static-page-hero">
          <p className="static-page-kicker">Version 1.0 - Last updated June 4, 2026</p>
          <h1>Information Security and Privacy Governance Policy</h1>
          <p>
            This policy describes how Risqly protects data, manages security risk, governs access,
            handles incidents, reviews suppliers, and maintains evidence for its own security posture.
          </p>
        </section>

        <section className="policy-document">
          <div className="policy-document__meta" aria-label="Policy metadata">
            <div>
              <span>Owner</span>
              <strong>Risqly management</strong>
            </div>
            <div>
              <span>Scope</span>
              <strong>Platform, profiles, dashboards, scoring systems, documents, and operations</strong>
            </div>
            <div>
              <span>Review cadence</span>
              <strong>At least annually and after material changes</strong>
            </div>
          </div>

          {policySections.map((section) => (
            <article className="policy-document__section" key={section.title}>
              <h2>{section.title}</h2>
              {section.paragraphs?.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              {section.bullets && (
                <ul>
                  {section.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              )}
            </article>
          ))}
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default SecurityGovernancePolicy;
